import { useEffect, useState } from 'react';
import { Button, Dialog, Switch } from '@/components/ui';
import {
  CONSENT_CATALOG,
  acceptAll,
  rejectAll,
  setConsent,
  useConsent,
  type ConsentCategoryInfo,
  type ConsentChoices,
} from '@/lib/consent';
import { CookieCatalogTable } from './CookieCatalogTable';

export interface CookieSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

interface CategoryRowProps {
  category: ConsentCategoryInfo;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function CategoryRow({ category, checked, onChange }: CategoryRowProps): JSX.Element {
  const headingId = `cookie-category-${category.id}`;
  return (
    <section className="cookie-category" aria-labelledby={headingId}>
      <div className="cookie-category__header">
        <h3 id={headingId} className="cookie-category__title">
          {category.title}
        </h3>
        {category.required ? (
          <Switch checked disabled readOnly aria-label={`${category.title}: sempre ativos`} label="Sempre ativos" />
        ) : (
          <Switch
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
            aria-label={category.title}
            label={checked ? 'Ativos' : 'Inativos'}
          />
        )}
      </div>
      <p className="cookie-category__description">{category.description}</p>
      {category.id === 'advertising' && (
        <p className="cookie-category__note">
          Ao desligar, o site deixa de carregar os anúncios. Cookies que o Google já gravou no
          domínio dele só saem pelas configurações do seu navegador.
        </p>
      )}
      <details className="cookie-category__details">
        <summary>Ver os itens ({category.items.length})</summary>
        <CookieCatalogTable caption={`Itens da categoria ${category.title}`} items={category.items} />
      </details>
    </section>
  );
}

/**
 * O painel de preferências: uma linha por categoria do catálogo, com o
 * interruptor (necessários travados), a descrição e a tabela do que cada uma
 * guarda. Fechar sem salvar não registra nada — nem aceite, nem recusa.
 */
export function CookieSettingsDialog({ open, onClose }: CookieSettingsDialogProps): JSX.Element | null {
  const consent = useConsent();
  const [draft, setDraft] = useState<ConsentChoices>(consent.choices);

  // Cada abertura parte da escolha vigente, não do rascunho abandonado da anterior.
  const current = consent.choices;
  useEffect(() => {
    if (open) setDraft(current);
  }, [open, current]);

  const finish = (apply: () => void): void => {
    apply();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title="Preferências de cookies"
      description="Escolha o que o site pode guardar no seu navegador. Os necessários ficam sempre ativos; os demais, só com a sua permissão."
      footer={
        <>
          <Button onClick={() => finish(rejectAll)}>Rejeitar não essenciais</Button>
          <Button onClick={() => finish(acceptAll)}>Aceitar todos</Button>
          <Button variant="primary" onClick={() => finish(() => setConsent(draft))}>
            Salvar preferências
          </Button>
        </>
      }
    >
      <div className="cookie-settings">
        {consent.gpc && (
          <p className="cookie-settings__gpc">
            Seu navegador envia o sinal Global Privacy Control: a publicidade começa desligada.
          </p>
        )}
        {CONSENT_CATALOG.map((category) => (
          <CategoryRow
            key={category.id}
            category={category}
            checked={category.required ? true : draft[category.id as keyof ConsentChoices]}
            onChange={(checked) => setDraft((prev) => ({ ...prev, [category.id]: checked }))}
          />
        ))}
      </div>
    </Dialog>
  );
}
